import { createFileRoute, Link } from '@tanstack/react-router'

export const Route = createFileRoute('/terms')({
  component: TermsPage,
})

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-100">{title}</h2>
      <div className="space-y-3 text-sm text-gray-600 leading-relaxed">{children}</div>
    </section>
  )
}

function Sub({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-semibold text-gray-800 mb-1">{title}</p>
      {children}
    </div>
  )
}

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-gray-100 bg-white/80 backdrop-blur">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="DistriHub" className="w-7 h-7 object-contain" />
            <span className="text-base font-bold tracking-tight">Distri<span className="text-rose-600">Hub</span></span>
          </Link>
          <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 font-medium">Sign in</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-14">
        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-rose-600 mb-2">Legal</p>
          <h1 className="text-3xl font-extrabold text-gray-900">Terms &amp; Conditions</h1>
          <p className="mt-2 text-sm text-gray-500">
            Jiamini Innovations · Last updated: 30 July 2026
          </p>
        </div>

        <Section title="1. Agreement to these terms">
          <p>
            By accessing or using any DistriHub product — including the DistriHub ERP web application
            (<strong>app.distrihub.co.ke</strong>), the DistriMarket mobile application, or any associated
            APIs or services — you agree to be bound by these Terms &amp; Conditions.
          </p>
          <p>
            If you are accepting on behalf of a business, you represent that you have authority to bind
            that business to these terms. If you do not agree, do not use our services.
          </p>
          <p>
            These terms are governed by and construed in accordance with the laws of Kenya. Any dispute
            arising under these terms shall be subject to the exclusive jurisdiction of the courts of Kenya.
          </p>
        </Section>

        <Section title="2. Who we are">
          <p>
            DistriHub is a product of <strong>Jiamini Innovations</strong>, a company registered in Kenya.
            References to "DistriHub", "we", "us", or "our" mean Jiamini Innovations. Our contact email
            is{' '}
            <a href="mailto:info@distrihub.co.ke" className="text-rose-600 hover:underline">info@distrihub.co.ke</a>.
          </p>
        </Section>

        <Section title="3. Our services">
          <Sub title="DistriHub ERP">
            <p>
              A cloud-based business management platform for distributors, retailers, and field sales teams.
              Features include inventory management, sales, procurement, payroll, accounting, field sales
              (route planning and van loading), and the DistriMarket seller portal.
            </p>
          </Sub>
          <Sub title="DistriMarket">
            <p>
              A social commerce marketplace available as a mobile application. DistriMarket connects
              DistriHub businesses with consumers, enabling product discovery, ordering, and payment.
            </p>
          </Sub>
        </Section>

        <Section title="4. Account registration">
          <ul className="list-disc ml-5 space-y-1">
            <li>
              <strong>Business accounts (ERP):</strong> you must provide accurate business information
              including business name, owner name, email, and phone number. You are responsible for
              maintaining the confidentiality of your login credentials.
            </li>
            <li>
              <strong>Staff accounts:</strong> staff members are invited by the business owner. Staff
              members must use their credentials only for purposes authorised by the employing business.
            </li>
            <li>
              <strong>Consumer accounts (DistriMarket):</strong> you must be at least 18 years old to
              register. You must provide a valid phone number or email address.
            </li>
            <li>
              You are responsible for all activity that occurs under your account. Notify us immediately
              at{' '}
              <a href="mailto:info@distrihub.co.ke" className="text-rose-600 hover:underline">info@distrihub.co.ke</a>{' '}
              if you suspect unauthorised access.
            </li>
          </ul>
        </Section>

        <Section title="5. Subscription plans and billing (ERP)">
          <Sub title="Plans">
            <p>
              DistriHub ERP is offered on tiered subscription plans (Free, Starter, Growth, Enterprise).
              Features available to your account depend on your active plan. Plan details are shown
              within the application.
            </p>
          </Sub>
          <Sub title="Billing">
            <ul className="list-disc ml-5 mt-1 space-y-1">
              <li>Subscriptions are billed on a monthly or annual basis as selected at checkout.</li>
              <li>Payments are processed through M-Pesa or other methods we make available from time to time.</li>
              <li>Subscription fees are non-refundable except where required by Kenyan consumer protection law.</li>
              <li>We will notify you at least 14 days before a material price change takes effect.</li>
              <li>Failure to pay may result in downgrade to the Free plan or suspension of your account.</li>
            </ul>
          </Sub>
          <Sub title="Free plan">
            <p>
              The Free plan is provided at no charge and includes limited features. We reserve the right
              to modify or discontinue the Free plan with 30 days' notice.
            </p>
          </Sub>
        </Section>

        <Section title="6. Acceptable use">
          <p>You agree not to use our services to:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Violate any applicable Kenyan law or regulation, including the Computer Misuse and Cybercrimes Act 2018.</li>
            <li>Upload, store, or transmit content that is unlawful, defamatory, obscene, or fraudulent.</li>
            <li>Impersonate another person or entity, or misrepresent your affiliation with any person or entity.</li>
            <li>Attempt to gain unauthorised access to any part of our platform or another user's account.</li>
            <li>Introduce malware, viruses, or any other harmful code into our systems.</li>
            <li>Scrape, crawl, or systematically extract data from our platform without express written permission.</li>
            <li>Resell or sublicense access to DistriHub services to third parties without our consent.</li>
            <li>Use our platform to facilitate pyramid schemes, fraudulent promotions, or misleading advertising.</li>
          </ul>
          <p>
            We reserve the right to suspend or terminate accounts that violate this acceptable use policy,
            without refund of any prepaid fees.
          </p>
        </Section>

        <Section title="7. Seller obligations (DistriMarket)">
          <p>Businesses operating a DistriMarket storefront agree to:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>List only genuine products that you are authorised to sell.</li>
            <li>Ensure product descriptions, prices, and photos are accurate and not misleading.</li>
            <li>Fulfil orders placed through DistriMarket in accordance with the delivery timelines and terms you have advertised.</li>
            <li>Handle customer complaints promptly and in good faith.</li>
            <li>Comply with all applicable consumer protection laws, including the Consumer Protection Act 2012 (Kenya).</li>
            <li>Collect and remit any applicable taxes (VAT, Withholding Tax) on your sales — DistriHub's eTIMS integration assists with this obligation but the legal responsibility remains with the seller.</li>
          </ul>
          <p>
            Jiamini Innovations is a technology platform and marketplace facilitator. We are not a party to
            the transaction between a seller and a buyer. Any dispute regarding product quality, delivery,
            or refunds is between the seller and the buyer, though we will provide reasonable assistance
            in mediation.
          </p>
        </Section>

        <Section title="8. Consumer obligations (DistriMarket)">
          <p>As a DistriMarket consumer you agree to:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Provide accurate delivery addresses and contact information when placing orders.</li>
            <li>Pay for orders you place. Failed or reversed payments may result in order cancellation.</li>
            <li>Use the platform's review and rating system honestly and in good faith.</li>
            <li>Not post content — reviews, posts, or messages — that is abusive, defamatory, or misleading.</li>
          </ul>
        </Section>

        <Section title="9. Intellectual property">
          <Sub title="Our intellectual property">
            <p>
              All software, designs, logos, trademarks, and content constituting the DistriHub and
              DistriMarket platforms are owned by or licensed to Jiamini Innovations. Nothing in these
              terms transfers any intellectual property rights to you.
            </p>
          </Sub>
          <Sub title="Your content">
            <p>
              You retain ownership of content you upload (product listings, photos, business posts, etc.).
              By uploading content, you grant Jiamini Innovations a non-exclusive, royalty-free, worldwide
              licence to store, display, and distribute that content solely for the purpose of operating
              and improving our services.
            </p>
          </Sub>
          <Sub title="Your business data">
            <p>
              Operational data you generate within the ERP (sales records, inventory, payroll, etc.) belongs
              to your business. We process it on your behalf and do not claim ownership over it. See our{' '}
              <Link to="/privacy" className="text-rose-600 hover:underline">Privacy Policy</Link>{' '}
              for data handling details.
            </p>
          </Sub>
        </Section>

        <Section title="10. Uptime and service availability">
          <p>
            We aim to provide highly available services but do not guarantee uninterrupted access. Planned
            maintenance will be communicated in advance where possible. We are not liable for losses caused
            by temporary unavailability of our services.
          </p>
          <p>
            We may add, modify, or discontinue features at any time. For material changes that reduce
            functionality, we will provide at least 30 days' notice to active subscribers.
          </p>
        </Section>

        <Section title="11. Third-party integrations">
          <p>
            Our services integrate with third-party providers including Safaricom M-Pesa, the Kenya Revenue
            Authority (KRA) eTIMS system, and others listed in our{' '}
            <Link to="/privacy" className="text-rose-600 hover:underline">Privacy Policy</Link>.
            These integrations are provided as-is, and their availability is subject to the third parties'
            own terms and uptime. We are not responsible for failures, errors, or downtime of third-party
            systems.
          </p>
        </Section>

        <Section title="12. Limitation of liability">
          <p>
            To the maximum extent permitted by Kenyan law, Jiamini Innovations and its directors, employees,
            and agents shall not be liable for:
          </p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Indirect, incidental, consequential, or punitive damages arising from your use of our services.</li>
            <li>Loss of profits, revenue, data, or business opportunity.</li>
            <li>Errors or inaccuracies in eTIMS submissions, tax calculations, or payroll computations where these result from incorrect data entered by you.</li>
            <li>Losses arising from unauthorised access to your account where such access results from your failure to protect your credentials.</li>
          </ul>
          <p>
            Our total aggregate liability to you for any claim arising under these terms shall not exceed
            the subscription fees you paid in the 3 months immediately preceding the event giving rise to
            the claim.
          </p>
          <p>
            Nothing in these terms limits liability for death or personal injury caused by negligence, or
            for fraud or fraudulent misrepresentation.
          </p>
        </Section>

        <Section title="13. Indemnification">
          <p>
            You agree to indemnify and hold harmless Jiamini Innovations from any claims, losses, or
            expenses (including legal fees) arising from your breach of these terms, your use of our
            services, your content, or your violation of any law or the rights of a third party.
          </p>
        </Section>

        <Section title="14. Termination">
          <Sub title="By you">
            <p>
              You may cancel your subscription at any time through the Plan &amp; Billing section of the
              ERP, or by emailing{' '}
              <a href="mailto:info@distrihub.co.ke" className="text-rose-600 hover:underline">info@distrihub.co.ke</a>.
              Cancellation takes effect at the end of the current billing period. No refunds are issued for
              the remaining portion of a prepaid period.
            </p>
          </Sub>
          <Sub title="By us">
            <p>
              We may suspend or terminate your account immediately if you breach these terms or engage in
              activity that poses a risk to our platform or other users. For non-material breaches, we will
              give you 14 days to remedy the breach before terminating.
            </p>
          </Sub>
          <Sub title="Effect of termination">
            <p>
              On termination, your access to paid features will be removed. Your business data is retained
              for the periods set out in our{' '}
              <Link to="/privacy" className="text-rose-600 hover:underline">Privacy Policy</Link>{' '}
              to comply with Kenyan legal requirements, after which it is permanently deleted. You may
              export your data at any time while your account is active.
            </p>
          </Sub>
        </Section>

        <Section title="15. Changes to these terms">
          <p>
            We may update these terms from time to time. We will notify active users of material changes
            by email at least 14 days before the change takes effect. Continued use of the platform after
            the effective date constitutes acceptance of the revised terms. If you do not accept a
            material change, you may cancel your account before the effective date.
          </p>
        </Section>

        <Section title="16. Governing law and disputes">
          <p>
            These terms are governed by the laws of the Republic of Kenya. Any dispute arising from or
            in connection with these terms — including disputes about their existence, validity, or
            termination — shall first be referred to good-faith negotiation between the parties. If not
            resolved within 30 days, the dispute shall be submitted to the exclusive jurisdiction of
            the courts of Nairobi, Kenya.
          </p>
        </Section>

        <Section title="17. Contact us">
          <p>
            For any questions about these terms, please contact:
          </p>
          <div className="bg-gray-50 rounded-xl p-4 text-sm">
            <p className="font-semibold text-gray-800">Jiamini Innovations</p>
            <p>Product: DistriHub</p>
            <p>Email: <a href="mailto:info@distrihub.co.ke" className="text-rose-600 hover:underline">info@distrihub.co.ke</a></p>
            <p>Country: Kenya</p>
          </div>
        </Section>
      </main>

      <footer className="border-t border-gray-100 py-6">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-400">
          <span>© {new Date().getFullYear()} Jiamini Innovations. All rights reserved.</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-gray-600">Privacy Policy</Link>
            <Link to="/" className="hover:text-gray-600">Back to home</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
